import {
  action,
  computed,
  makeObservable,
  runInAction,
  observable,
  onBecomeUnobserved,
  onBecomeObserved,
} from "mobx";
import { AppSubStoreArgs } from "@/store/types";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import { ApiKeyModelData, ApiKeyRecord } from "@/store/api-keys/types";
import { ApiKeyObservable } from "@/store/api-keys/ApiKeyObservable";
import { CreateApiKeyOperation } from "@/store/sync/operations/api-keys/CreateApiKeyOperation";
import { Uuid } from "@/domains/global/identifiers";
import { isEmpty } from "lodash-es";
import { liveQuery } from "dexie";
import { Subscription } from "dexie";
import { Maybe } from "@/domains/common/types";
import { DeleteApiKeyOperation } from "@/store/sync/operations/api-keys/DeleteApiKeyOperation";
import { UpdateApiKeyOperation } from "@/store/sync/operations/api-keys/UpdateApiKeyOperation";

export class AppStoreApiKeysStore extends BaseSyncModelStore<ApiKeyObservable, ApiKeyModelData> {
  liveQuerySubscription: Maybe<Subscription>;
  subscribedApiKeyRows: ApiKeyRecord[] = [];
  private initialLoaded: boolean = false;

  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.ApiKey, ...injectedDeps });

    makeObservable<
      this,
      "subscribeLiveQuery" | "unsubscribeLiveQuery" | "setIsReady" | "initialLoaded"
    >(this, {
      updateApiKey: true,
      deleteApiKey: true,
      allActiveApiKeys: computed,
      subscribedApiKeyRows: true,

      liveQuerySubscription: observable,
      subscribeLiveQuery: action,
      unsubscribeLiveQuery: action,

      initialLoaded: observable,
      isReady: computed,
      setIsReady: action,

      createSyncModel: false,
      createApiKey: action,
      isCreatingApiKey: computed,
    });

    onBecomeObserved(this, "allActiveApiKeys", () => this.subscribeLiveQuery());
    onBecomeUnobserved(this, "allActiveApiKeys", () => this.unsubscribeLiveQuery());
    onBecomeObserved(this, "isReady", () => this.subscribeLiveQuery());
  }

  get isReady() {
    return this.initialLoaded;
  }

  createSyncModel(data: SyncUpdateValue<ApiKeyModelData>): ApiKeyObservable {
    return new ApiKeyObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  get isCreatingApiKey() {
    const apiKeyOperations =
      this.store.sync.actionQueue.operationsByOperationKind.get("CREATE_API_KEY");

    if (isEmpty(apiKeyOperations)) {
      return false;
    }

    return true;
  }

  /**
   * Actions
   */

  async createApiKey({
    id,
    title,
    description,
    secretKeyToken,
  }: {
    id: Uuid;
    secretKeyToken: Uuid;
    title: string | null;
    description: string | null;
  }) {
    await new CreateApiKeyOperation({
      store: this.store,
      payload: {
        id,
        secret_key_token: secretKeyToken,
        title,
        description,
      },
    }).execute();
  }

  /**
   * @TODO - We will use his for supporting `nicknaming` API keys in the future.
   */
  async updateApiKey({ id, title }: { id: Uuid; title: string | null }) {
    await new UpdateApiKeyOperation({
      store: this.store,
      payload: { id, title },
    }).execute();
  }

  async deleteApiKey({ id }: { id: Uuid }) {
    await new DeleteApiKeyOperation({
      store: this.store,
      payload: { id },
    }).execute();
  }

  /**
   * Subscriptions
   */

  get allActiveApiKeys(): ApiKeyObservable[] {
    const observables = this.subscribedApiKeyRows.map(row => this.store.apiKeys.get(row.id));

    return (
      observables
        .filter(observable => observable !== undefined)
        .filter(observable => !observable.isRevoked)
        // TODO REMOVE
        .filter(observable => !!observable.redactedToken)
    );
  }

  private subscribeLiveQuery() {
    this.setIsReady(false);
    this.liveQuerySubscription?.unsubscribe();

    this.liveQuerySubscription = liveQuery(() => {
      return this.store.apiKeys.localTable.toCollection().keys();
    }).subscribe({
      next: items => {
        runInAction(() => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          this.subscribedApiKeyRows = items.map((item: any) => ({ id: item }));
          this.setIsReady(true);
        });
      },
    });
  }

  private unsubscribeLiveQuery() {
    this.setIsReady(false);
    this.liveQuerySubscription?.unsubscribe();
  }

  private setIsReady(value: boolean) {
    this.initialLoaded = value;
  }
}
